import { BIRTHDAYS } from "@/data/birthdays";

const Body = ({ children }) => <p className="text-sm leading-relaxed text-cypress-cream/80">{children}</p>;

const Quip = ({ children }) => (
  <p className="border-l-2 border-cypress-gold/50 pl-3 text-sm italic text-cypress-gold/90">{children}</p>
);

const Callout = ({ title, children }) => (
  <div className="rounded-lg border border-cypress-gold/40 bg-cypress-gold/10 px-4 py-3">
    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-cypress-gold">{title}</p>
    <div className="mt-2 text-sm text-cypress-cream/80">{children}</div>
  </div>
);

const Block = ({ title, children }) => (
  <div>
    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-cypress-gold">{title}</p>
    <div className="mt-1.5 space-y-1.5">{children}</div>
  </div>
);

export const RULES_CONTENT = {
  Money: (
    <Body>Check in and pay Nick in the Pro Shop before 7:00 AM. If you arrive after 7:00 AM, pay your scorekeeper.</Body>
  ),
  "Par In (Holes 15-18)": (
    <>
      <Body>All golfers who par in will share the Par In Pot.</Body>
      <Body>If you are the only player to make the Par In, the pot is yours.</Body>
      <Quip>The gimme you&rsquo;ve been taking all year suddenly looks a lot less cute on Hole 17.</Quip>
      <Callout title="Important">
        <ul className="list-disc space-y-1 pl-4">
          <li>All holes must be putted out.</li>
          <li>NO GIMMIES.</li>
          <li>If you fail to putt out or fail to finish any hole from 15-18, you forfeit your chance to win the Par In Pot.</li>
        </ul>
      </Callout>
    </>
  ),
  "Rabbit & Squirrel": (
    <>
      <Body>Settled using the scorecard tally at the end of the round.</Body>
      <ul className="list-disc space-y-1 pl-5 text-sm text-cypress-cream/80">
        <li>Win the Rabbit &rarr; receive <span className="font-mono font-bold text-cypress-gold">$5</span> from each player.</li>
        <li>Win the Squirrel &rarr; receive <span className="font-mono font-bold text-cypress-gold">$2</span> from each player.</li>
      </ul>
      <Body>One Rabbit winner and one Squirrel winner per round — two different players. Nobody takes both.</Body>
      <Quip>Simple enough. Unless you&rsquo;re the squirrel.</Quip>
    </>
  ),
  Payouts: (
    <>
      <Body>Payouts will take place immediately after the round in the clubhouse.</Body>
      <div className="grid gap-3 sm:grid-cols-2">
        <Callout title="Scorekeepers">
          <p className="mb-1">Scorekeepers are responsible for:</p>
          <ol className="list-decimal space-y-1 pl-4">
            <li>Reviewing all scorecards.</li>
            <li>Confirming all Dots.</li>
            <li>Confirming the final Dot count.</li>
            <li>Turning in the completed information for payout.</li>
          </ol>
        </Callout>
        <Callout title="Dot Value">
          <p className="font-mono font-bold text-cypress-gold">Total Pot &divide; Total Dots = Value of Each Dot</p>
        </Callout>
      </div>
      <Body>
        The final group — typically Nick&rsquo;s group — should come directly into the clubhouse after finishing. This allows anyone who
        needs to leave immediately after the round to receive their payout without waiting around.
      </Body>
      <Quip>We want fast math, accurate money, and zero awkward clubhouse hostage situations.</Quip>
    </>
  ),
  "Relief Rules": (
    <>
      <Block title="Burrowing Animal Hole">
        <Body>
          When entitled to free relief from an abnormal course condition outside a penalty area, follow USGA Rule 16.1. Determine the
          nearest point of complete relief, no nearer the hole, and drop within one club-length of that point.
        </Body>
      </Block>
      <Block title="Tree Root Relief — Skins Local Rule">
        <Body>
          For our Skins game, if your ball is on or immediately against an exposed tree root, and playing the ball could reasonably cause
          injury or damage to a club, you may take free relief. Drop the ball as close as possible to its original position, without being
          on the root and no nearer the hole.
        </Body>
      </Block>
      <Block title="The Purpose of This Rule">
        <Body>This relief exists strictly to prevent injury or damage to a club.</Body>
        <Body>It is NOT intended to provide a better lie, a clearer shot, a better angle, or a more convenient stance.</Body>
        <Body>There is no relief simply because your stance is on a root.</Body>
      </Block>
      <Callout title="Important">
        Tree root relief is NOT a USGA Rule. It is a local rule specifically for our Skins game.
      </Callout>
    </>
  ),
  Birthdays: (
    <>
      <Body>{BIRTHDAYS.note}</Body>
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-cypress-gold">
          {BIRTHDAYS.month} birthdays 🎉
        </p>
        <div className="mt-2 flex flex-wrap gap-2" data-testid="birthday-pills">
          {BIRTHDAYS.names.map((p) => (
            <span
              key={p.name}
              data-testid={`birthday-pill-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="rounded-full border border-cypress-gold/40 bg-cypress-green/50 px-3.5 py-1.5 text-sm font-semibold tracking-tight"
            >
              {BIRTHDAYS.emoji.pill} {p.name}
              <span className="block text-center font-mono text-[10px] font-medium tracking-normal text-cypress-cream/60">
                {p.date}
              </span>
            </span>
          ))}
        </div>
      </div>
      {BIRTHDAYS.party && (
        <div className="rounded-xl bg-cypress-gold px-4 py-4 text-center shadow-[0_0_30px_rgba(201,162,39,0.25)]" data-testid="birthday-party-block">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-cypress-night/70">The Party</p>
          <p className="mt-1 text-lg font-bold text-cypress-night sm:text-xl">
            {BIRTHDAYS.emoji.party} {BIRTHDAYS.party} {BIRTHDAYS.emoji.party}
          </p>
        </div>
      )}
    </>
  ),
};
